import type { ReactNode } from 'react';
import type { MetricPair, TrendPoint } from './types';

export function formatNumber(value: number | null | undefined, suffix = '') {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(value) + suffix;
}

export function formatDate(value: string | null | undefined, withTime = false) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
    timeZone: 'Asia/Kolkata',
  }).format(new Date(value));
}

export function MetricCard(props: {
  label: string; value: ReactNode; note?: string; tone?: 'default' | 'good' | 'warn';
}) {
  return (
    <article className={`metric-card ${props.tone ?? 'default'}`}>
      <span>{props.label}</span>
      <strong>{props.value}</strong>
      {props.note && <small>{props.note}</small>}
    </article>
  );
}

export function Panel(props: { title: string; subtitle?: string; children: ReactNode; action?: ReactNode }) {
  return (
    <section className="panel">
      <header className="panel-header">
        <div>
          <h2>{props.title}</h2>
          {props.subtitle && <p>{props.subtitle}</p>}
        </div>
        {props.action}
      </header>
      {props.children}
    </section>
  );
}

export function StatusPill({ value }: { value: string }) {
  const normalized = value.toLowerCase().replaceAll('_', '-');
  return <span className={`status-pill status-${normalized}`}>{value.replaceAll('_', ' ')}</span>;
}

export function BarList({ rows }: { rows: MetricPair[] }) {
  const max = Math.max(...rows.map((row) => Number(row.value)), 1);
  if (!rows.length) return <EmptyState label="No activity in this period" />;
  return (
    <div className="bar-list">
      {rows.map((row) => (
        <div className="bar-row" key={row.label}>
          <div className="bar-label"><span>{row.label.replaceAll('_', ' ')}</span><strong>{formatNumber(row.value)}</strong></div>
          <div className="bar-track"><progress value={Number(row.value)} max={max}>{Number(row.value)}</progress></div>
        </div>
      ))}
    </div>
  );
}

// `trend.day` is a bare IST calendar date ("2026-09-02"). `new Date()` reads
// that as UTC midnight, so formatting it in the browser's zone shifts the label
// a day back for any admin west of UTC — while the panel above it claims India
// Standard Time. Pin the zone the way formatDate already does.
function axisDay(day: string) {
  const calendarDay = String(day).slice(0, 10);
  const parsed = new Date(`${calendarDay}T00:00:00+05:30`);
  if (Number.isNaN(parsed.getTime())) return calendarDay;
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata',
  }).format(parsed);
}

export function LineChart({ rows }: { rows: TrendPoint[] }) {
  const width = 760;
  const height = 250;
  const pad = 26;
  if (!rows.length) return <EmptyState label="No trend data yet" />;
  const max = Math.max(...rows.flatMap((r) => [Number(r.new_users), Number(r.rides_posted), Number(r.passenger_journeys)]), 1);
  const x = (index: number) => pad + index * ((width - pad * 2) / Math.max(rows.length - 1, 1));
  const y = (value: number) => height - pad - (Number(value) / max) * (height - pad * 2);
  const path = (field: keyof TrendPoint) => rows.map((row, i) =>
    `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(Number(row[field])).toFixed(1)}`
  ).join(' ');
  const first = rows[0]!;
  const last = rows[rows.length - 1]!;
  return (
    <div>
      <div className="chart-legend">
        <span><i className="legend-users" />New users</span>
        <span><i className="legend-rides" />Rides posted</span>
        <span><i className="legend-journeys" />Completed journeys</span>
      </div>
      <svg className="line-chart" viewBox={`0 0 ${width} ${height}`} role="img"
        aria-label="Daily new users, rides posted, and completed passenger journeys">
        {[0, .25, .5, .75, 1].map((ratio) => (
          <line key={ratio} x1={pad} x2={width - pad} y1={y(max * ratio)} y2={y(max * ratio)} className="grid-line" />
        ))}
        <path d={path('new_users')} className="line users" />
        <path d={path('rides_posted')} className="line rides" />
        <path d={path('passenger_journeys')} className="line journeys" />
        <text x={pad} y={height - 5}>{axisDay(first.day)}</text>
        <text x={width - pad} y={height - 5} textAnchor="end">{axisDay(last.day)}</text>
      </svg>
    </div>
  );
}

export function EmptyState({ label }: { label: string }) {
  return <div className="empty-state"><span aria-hidden="true">–</span><p>{label}</p></div>;
}

export function LoadingBlock() {
  return <div className="loading-block" aria-label="Loading"><span /><span /><span /></div>;
}

export function ErrorBlock({ message, retry }: { message: string; retry: () => void }) {
  return (
    <div className="error-block" role="alert">
      <div><strong>Couldn’t load this view</strong><p>{message}</p></div>
      <button className="secondary-button" onClick={retry}>Try again</button>
    </div>
  );
}

